import type { Location } from 'history'
import { useSelector } from 'react-redux'
import {
  useLocation,
  useRouteMatch,
  Redirect,
  generatePath,
} from 'react-router-dom'

import { useDecodedAccessToken } from '@/app/AuthProvider'
import type { RootState } from '@store/rootReducer'

interface MyAccountRedirectProps {
  children: React.ReactNode
  paramName: 'accountId' | 'personId'
  /**
   * Opt in to correcting stale route IDs before rendering the route.
   */
  validateExistingParam?: boolean
}

const MyAccountRedirect = ({
  children,
  paramName,
  validateExistingParam = false,
}: MyAccountRedirectProps) => {
  // react-router-dom's v5 types don't resolve under tsgolint, so this hook comes
  // back as an error type. Assert the `history` package's own type.
  const location = useLocation() as Location
  const { path, params } = useRouteMatch<{
    accountId?: string
    personId?: string
  }>()

  // Get the actual IDs directly from the token (not from URL params)
  const {
    accountId: tokenAccountId,
    personId: tokenPersonId,
    accountRoles,
  } = useDecodedAccessToken() ?? {}

  // During registration, user info may exist in signup state
  const { accountId: signupAccountId, _id: signupPersonId } = useSelector(
    ({ auth }: RootState) => auth.signup.submitEmail
  )

  // Directly render when URL doesn't contain "/my/"
  if (!location.pathname.includes('/my/')) {
    if (validateExistingParam) {
      // Correct stale route IDs before downstream code trusts the URL param.
      const paramValue = params[paramName]
      const actualId =
        paramName === 'accountId'
          ? tokenAccountId || signupAccountId
          : tokenPersonId || signupPersonId
      const allowedIds = (
        paramName === 'accountId'
          ? [
              tokenAccountId,
              signupAccountId,
              ...Object.keys(accountRoles ?? {}),
            ]
          : [tokenPersonId, signupPersonId]
      ).filter((id): id is string => id != null)
      if (paramValue && actualId && !allowedIds.includes(paramValue)) {
        return (
          <Redirect
            to={
              generatePath(path, { ...params, [paramName]: actualId }) +
              location.search
            }
          />
        )
      }
    }
    return <>{children}</>
  }

  // For "/my/" urls, we need to get the actual ID from the Redux store or signup state
  // Get the actual ID from store
  const actualAccountId = tokenAccountId || signupAccountId
  const actualPersonId = tokenPersonId || signupPersonId
  const actualId = paramName === 'accountId' ? actualAccountId : actualPersonId

  // If no actual ID found, redirect to sign-in
  if (!actualId) {
    const redirectAfterLogin = encodeURIComponent(
      location.pathname + location.search
    )
    return <Redirect to={`/signin?redirectAfterLogin=${redirectAfterLogin}`} />
  }

  // Replace "my" with the actual ID in the pathname
  const newPathname = location.pathname.replace('/my/', `/${actualId}/`)

  // Redirect to the actual ID with preserved query parameters
  return <Redirect to={newPathname + location.search} />
}

export default MyAccountRedirect
